import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonVariant,
  Icon,
  InputVariant,
  Link,
  ModalBody,
  ModalCustom,
  ModalFooter,
  ModalHeader,
  PhoneIcon,
  TextField,
  UserOutlineIcon,
} from '@/components';
import { Post } from '@/features/blog/const';
import { PagePath } from '@/layouts/shared';
import { yupResolver } from '@hookform/resolvers/yup';
import { Trans, useTranslation } from 'next-i18next';
import * as yup from 'yup';

import { useForm } from 'react-hook-form';
import { useSignUpAmbassadorQuery } from './api';
import toast from 'react-hot-toast';
import { useMasterData } from '@/contexts/profile';

interface IAmbassadorRegistrationFormProps {
  article: Post;
}

const AmbassadorRegistrationForm = (
  props: IAmbassadorRegistrationFormProps
) => {
  const { article } = props;
  const { t } = useTranslation(['layout']);
  const masterData: any = useMasterData();
  const ambassador_info = masterData?.ambassador_info;

  const [isShowSuccessMsg, setIsShowSuccessMsg] = useState<boolean>(false);
  const [isShowForm, setIsShowForm] = useState<boolean>(false);

  useEffect(() => {
    const getSlugFromUrl = (url: string) => {
      if (!url) return '';
      const parts = url.split('/').filter((el) => !!el);
      return parts[parts.length - 1];
    };

    const slug = getSlugFromUrl(ambassador_info?.url);

    setIsShowForm(slug === article.slug);
  }, [article?.slug, ambassador_info?.url]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    defaultValues: { phone: '', fullname: '' },
    resolver: yupResolver(
      yup.object({
        phone: yup
          .string()
          .required(t('ambassador-register.message.phone-required')),
        fullname: yup
          .string()
          .required(t('ambassador-register.message.fullname-required')),
      })
    ),
  });

  const { mutate, isLoading } = useSignUpAmbassadorQuery({
    onSuccess: () => {
      setIsShowSuccessMsg(true);
    },
    onError: ({ message }) => {
      toast.error(message);
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutate({ ...data });
  });

  const keyPress = (e: any) => {
    if (e.keyCode === 13) {
      onSubmit();
    }
  };

  if (!isShowForm) {
    return <></>;
  }

  return (
    <>
      <div className="tw-flex tw-flex-col tw-w-full sm:tw-w-[440px] tw-mx-auto tw-mt-6 tw-px-2 lg:tw-p-4 tw-rounded-xl">
        <h3 className="tw-text-center tw-text-xl tw-font-semibold">
          {t('ambassador-register.title')}
        </h3>
        <div className="tw-flex tw-flex-col tw-mt-5">
          <TextField
            name="fullname"
            control={control}
            startAdornment={
              <Icon
                icon={<UserOutlineIcon className="tw-text-primary-main" />}
              />
            }
            variant={InputVariant.Outlined}
            error={errors.fullname?.message}
            containerClassName="tw-h-[50px] tw-px-4 tw-rounded-lg"
            errorMessageClassName="tw-px-4"
            placeholder={t('ambassador-register.form.label.fullname')}
            onKeyDown={keyPress}
          />
          <TextField
            name="phone"
            control={control}
            startAdornment={
              <Icon icon={<PhoneIcon className="tw-text-primary-main" />} />
            }
            variant={InputVariant.Outlined}
            error={errors.phone?.message}
            containerClassName="tw-h-[50px] tw-px-4 tw-rounded-lg tw-mt-6"
            errorMessageClassName="tw-px-4"
            placeholder={t('ambassador-register.form.label.phone')}
            onKeyDown={keyPress}
          />
        </div>
        <div className="tw-flex tw-flex-col tw-mt-6">
          <p>
            <Trans
              t={t}
              i18nKey="ambassador-register.label.policies"
              components={[
                <Link
                  url={{ pathname: PagePath.PrivacyPolicyPage }}
                  key="ambassador-register.label.policies"
                />,
              ]}
            />
          </p>
          <Button
            loading={isLoading}
            onClick={onSubmit}
            className="tw-mt-4 tw-h-[50px] tw-rounded-lg"
          >
            {t('ambassador-register.btn.submit')}
          </Button>
        </div>
      </div>
      {isShowSuccessMsg && (
        <ModalCustom className="tw-w-[360px]">
          <ModalHeader>
            <div className="tw-font-bold tw-text-lg tw-text-center">
              {t('ambassador-register.modal.register-success-title')}
            </div>
          </ModalHeader>
          <ModalBody>
            <p className="tw-text-center">
              {t('ambassador-register.modal.register-success-description')}
            </p>
          </ModalBody>
          <ModalFooter>
            <div className="tw-flex tw-justify-end tw-gap-3">
              <Button
                variant={ButtonVariant.Outlined}
                className="tw-text-primary-main tw-font-bold"
                onClick={() => setIsShowSuccessMsg(false)}
              >
                OK
              </Button>
            </div>
          </ModalFooter>
        </ModalCustom>
      )}
    </>
  );
};

export default AmbassadorRegistrationForm;
